import { useMany, useTranslate, type CrudFilter } from '@refinedev/core';
import { DateField, List } from '@refinedev/antd';
import { Alert, Table } from 'antd';
import {
  RoyaltyReportStatus,
  type PublisherListOut,
  type RoyaltyReportOut,
} from '@/api/generated/schemas';
import { orDash } from '@/components/common/show/shared';
import { DateRangeFilter } from '@/components/common/DateRangeFilter';
import { MonthFilter } from '@/components/common/MonthFilter';
import { SelectFilter } from '@/components/common/SelectFilter';
import { RoyaltyReportActions } from '@/components/settlement/RoyaltyReportActions';
import { RoyaltyReportStatusTag } from '@/components/settlement/tags';
import { PublisherIconLink } from '@/components/user_catalog/publishers/PublisherIconLink';
import { PublisherSelectFilter } from '@/components/user_catalog/publishers/PublisherSelectFilter';
import { useAdminTable } from '@/hooks/useAdminTable';
import { useTableRowNavigateToShow } from '@/hooks/useTableRowNavigateToShow';
import { isFieldFiltered } from '@/utils/filters';
import { getSortOrder } from '@/utils/sorters';
import { formatLedger } from '@/utils/money';
import { HintLabel } from '@/components/common/HintLabel';
import { PayableAmount } from '@/components/settlement/PayableAmount';
import { NUMERIC_STYLE } from '@/components/settlement/numeric';
import { currentStatements } from './statements';

const T = 'settlement.royalty_reports';

export const RoyaltyReportsList = () => {
  const t = useTranslate();
  const rowNav = useTableRowNavigateToShow();

  const { tableProps, sorters, filters, setFilters } =
    useAdminTable<RoyaltyReportOut>({
      pagination: { pageSize: 20 },
      resource: 'settlement.royalty_reports',
      syncWithLocation: true,
      sorters: {
        initial: [{ field: 'period', order: 'desc' }],
      },
    });

  const rows = tableProps.dataSource ?? [];

  // Batched once per page: the row components take the record and skip their
  // own fetch, so a page costs one extra request instead of one per row.
  const publisherIds = [
    ...new Set(rows.map((r) => r.publisher_id).filter(Boolean)),
  ];

  const { result: publishersResult } = useMany<PublisherListOut>({
    resource: 'user_catalog.publishers',
    ids: publisherIds,
    queryOptions: { enabled: publisherIds.length > 0 },
  });

  const publisherMap = new Map<number, PublisherListOut>(
    (publishersResult?.data ?? []).map((p: PublisherListOut) => [p.id, p])
  );

  return (
    <List canCreate={false}>
      <Alert
        type="warning"
        showIcon
        message={t(`${T}.intro`)}
        style={{ marginBottom: 16 }}
      />
      <Table
        {...rowNav('settlement.royalty_reports', tableProps)}
        pagination={{ ...tableProps.pagination, simple: true }}
        rowKey="id"
      >
        <Table.Column
          dataIndex="period"
          title={t(`${T}.fields.period`)}
          sorter
          sortOrder={getSortOrder(sorters, 'period')}
          filtered={isFieldFiltered(filters as CrudFilter[], 'period')}
          filterDropdown={({ confirm, clearFilters }) => (
            <MonthFilter
              field="period"
              placeholder={t(`${T}.fields.period`)}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(value: string) => (
            <DateField value={value} format="YYYY-MM" />
          )}
        />
        <Table.Column
          dataIndex="publisher_id"
          title={t(`${T}.fields.publisher_id`)}
          sorter
          sortOrder={getSortOrder(sorters, 'publisher_id')}
          filtered={isFieldFiltered(filters as CrudFilter[], 'publisher_id')}
          filterDropdown={({ confirm, clearFilters }) => (
            <PublisherSelectFilter
              field="publisher_id"
              only="parents"
              placeholder={t(`${T}.fields.publisher_id`)}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(value: number) =>
            orDash(
              value != null && (
                <PublisherIconLink
                  publisherId={value}
                  publisher={publisherMap.get(value)}
                />
              )
            )
          }
        />
        <Table.Column
          key="publishers_count"
          title={t(`${T}.fields.publishers_count`)}
          width={90}
          render={(_, record: RoyaltyReportOut) =>
            currentStatements(record.statements).length
          }
        />
        {/* Neither amount is in the endpoint's sort enum. */}
        <Table.Column
          dataIndex="amount"
          title={
            <HintLabel
              label={t(`${T}.fields.amount_ledger`)}
              hint={t('settlement.hints.amount_ledger')}
            />
          }
          align="right"
          render={(value: string, record: RoyaltyReportOut) => (
            <span style={NUMERIC_STYLE}>
              {formatLedger(value, record.currency)}
            </span>
          )}
        />
        <Table.Column
          dataIndex="payable_amount"
          title={
            <HintLabel
              label={t(`${T}.fields.payable_amount`)}
              hint={t('settlement.hints.payable_amount')}
            />
          }
          align="right"
          render={(value: string, record: RoyaltyReportOut) => (
            <PayableAmount
              amount={value}
              roundingDelta={record.rounding_delta}
              currency={record.currency}
            />
          )}
        />
        <Table.Column
          dataIndex="status"
          title={t(`${T}.fields.status`)}
          sorter
          sortOrder={getSortOrder(sorters, 'status')}
          filtered={isFieldFiltered(filters as CrudFilter[], 'status')}
          filterDropdown={({ confirm, clearFilters }) => (
            <SelectFilter
              field="status"
              placeholder={t(`${T}.fields.status`)}
              options={Object.values(RoyaltyReportStatus).map((value) => ({
                label: t(`${T}.status.${value}`),
                value,
              }))}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(value: RoyaltyReportStatus) => (
            <RoyaltyReportStatusTag status={value} />
          )}
        />
        <Table.Column
          dataIndex="created_at"
          title={t('base.fields.created_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'created_at')}
          filtered={isFieldFiltered(filters as CrudFilter[], 'created_at')}
          filterDropdown={({ confirm, clearFilters }) => (
            <DateRangeFilter
              field="created_at"
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(value: string) =>
            orDash(value && <DateField value={value} />)
          }
        />
        {/* Renders nothing past `draft` — the component owns that rule. */}
        <Table.Column
          title={t('actions.column')}
          dataIndex="actions"
          render={(_, record: RoyaltyReportOut) => (
            <RoyaltyReportActions record={record} size="small" />
          )}
        />
      </Table>
    </List>
  );
};
